import React, { useContext, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';

type WeglotType = {
  getAvailableLanguages: () => string[];
  getCurrentLang: () => string;
  initialize: (options: {
    api_key: string;
    auto_switch_fallback?: string;
    auto_switch?: boolean;
    cache?: boolean;
    dynamic?: string[];
    excluded_blocks?: Array<{ value: string }>;
    extra_definitions?: Array<{
      attribute?: string;
      selector: string;
      type: number;
    }>;
    hide_switcher?: boolean;
    translate_iframes?: string[];
    wait_transition?: boolean;
  }) => void;
  initialized: boolean;
  off: (event: string, callback: (lang: string) => void) => void;
  on: (event: string, callback: (lang: string) => void) => void;
  options: any;
  translate: (
    options: {
      words: Array<{ t: number; w: string }>;
      languageTo: string;
    },
    callback: (translation: string) => void,
  ) => void;
  setup: () => void;
  switchTo: (lang: string) => void;
};

declare global {
  interface Window {
    Weglot?: WeglotType;
  }
}

const DEFAULT_WEGLOT_LANGUAGE = 'en';

const WeglotContext = React.createContext<{
  weglot: React.MutableRefObject<WeglotType | undefined>;
  language: string;
}>({ weglot: { current: undefined }, language: DEFAULT_WEGLOT_LANGUAGE });

const WeglotProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const weglot = useRef<WeglotType>(undefined);
  const [language] = React.useState<string>(
    Cookies.get('language') || DEFAULT_WEGLOT_LANGUAGE,
  );

  const [ready, setReady] = React.useState(false);

  useEffect(() => {
    let offInitialized: ((lang: string) => void) | undefined;

    if (window.Weglot) {
      weglot.current = window.Weglot;

      // If Weglot was already initialized (HMR or fast refresh), unblock immediately
      if (weglot.current.initialized) {
        // Make sure language is correct, then mark ready
        weglot.current.switchTo(Cookies.get('language') || DEFAULT_WEGLOT_LANGUAGE);
        setReady(true);
      } else {
        weglot.current.initialize({
          api_key: process.env.REACT_APP_WEGLLOT_KEY || '',
          cache: false,
          dynamic: [
            'button', 'a', 'div', 'span', 'p',
            'h1', 'h2', 'h3', 'h4', 'label',
            '.toast-message',
          ],
          excluded_blocks: [{ value: '.no-translate' }],
          hide_switcher: true,
          wait_transition: true,
        });

        const onInitialized = () => {
          weglot.current?.switchTo(
            Cookies.get('language') || DEFAULT_WEGLOT_LANGUAGE,
          );
          setReady(true);
        };

        weglot.current.on('initialized', onInitialized);
        offInitialized = onInitialized;
      }
    } else {
      weglot.current = undefined;
      console.warn('Weglot is not available on the window object.');
      setReady(true);
    }

    return () => {
      if (weglot.current && offInitialized) {
        weglot.current.off('initialized', offInitialized);
      }
    };
  }, []);

  if (!ready) {
    return null;
  }

  return (
    <WeglotContext.Provider value={{ weglot, language }}>
      {children}
    </WeglotContext.Provider>
  );
};

export const useWeglot = () => useContext(WeglotContext);

export default WeglotProvider;
